import React, { useCallback, useEffect, useMemo, useState } from 'react';
import store from './journalStore';
import PhotoImg from './PhotoImg';
import Slideshow from './Slideshow';
import { VoiceClip } from './VoiceNotes';

/* Two-sided timeline: Tanush's notes on the LEFT, Anusha's on the RIGHT.
   A date's photos are gathered into a single centered STACK — tap it to play
   a slideshow of all that day's photos. */

const prettyDate = (ds) => {
  const [y, m, dd] = ds.split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
  });
};
const prettyTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};
const byCreated = (a, b) => (a.createdAt < b.createdAt ? -1 : 1);

export default function Timeline({ passcode }) {
  const [data, setData] = useState({ entriesByDate: {}, photos: [] });
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('desc');
  const [show, setShow] = useState(null); // { photos, index, autoplay }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await store.all(passcode));
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [passcode]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const photosByDate = {};
    (data.photos || []).forEach((p) => {
      (photosByDate[p.date] = photosByDate[p.date] || []).push(p);
    });
    const voicesByDate = {};
    (data.voices || []).forEach((v) => {
      (voicesByDate[v.date] = voicesByDate[v.date] || []).push(v);
    });
    const dates = [
      ...new Set([
        ...Object.keys(data.entriesByDate || {}),
        ...Object.keys(photosByDate),
        ...Object.keys(voicesByDate),
      ]),
    ];
    dates.sort((a, b) => (a < b ? (order === 'desc' ? 1 : -1) : order === 'desc' ? -1 : 1));
    return dates.map((date) => ({
      date,
      photos: (photosByDate[date] || []).slice().sort(byCreated),
      voices: (voicesByDate[date] || []).slice().sort(byCreated),
      notes: ((data.entriesByDate || {})[date] || []).slice().sort(byCreated),
    }));
  }, [data, order]);

  const openShow = (photos, index = 0, autoplay = true) => setShow({ photos, index, autoplay });
  const closeShow = () => {
    setShow(null);
    load(); // reflect any removals / date-moves made inside the slideshow
  };

  if (loading) return <div className="aj-muted">Loading our story…</div>;
  if (!groups.length) {
    return <div className="aj-muted">Your timeline is empty — add a note or photo and it'll appear here. 💕</div>;
  }

  return (
    <div className="aj-timeline">
      <div className="aj-tl-toolbar">
        <span className="aj-tl-count">
          {groups.length} day{groups.length > 1 ? 's' : ''} of us 💞
        </span>
        <button
          type="button"
          className="aj-tl-order"
          onClick={() => setOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
        >
          {order === 'desc' ? 'Newest first ↓' : 'From the start ↑'}
        </button>
      </div>

      <div className="aj-tl2">
        {groups.map((g) => (
          <React.Fragment key={g.date}>
            <div className="aj-tl-sep">
              <span>{prettyDate(g.date)}</span>
            </div>

            {g.photos.length > 0 && (
              <div className="aj-tl-stackwrap">
                <button
                  type="button"
                  className={`aj-stack${g.photos.length > 1 ? ' multi' : ''}`}
                  onClick={() => openShow(g.photos, 0, true)}
                  aria-label={`${g.photos.length} photo${g.photos.length > 1 ? 's' : ''} — play slideshow`}
                >
                  {g.photos.slice(0, 3).map((p, k) => (
                    <span className={`aj-stack-card s${k}`} key={p.id}>
                      <PhotoImg passcode={passcode} id={p.id} className="aj-stack-img" alt="memory" />
                    </span>
                  ))}
                  <span className="aj-stack-badge">
                    {g.photos.length > 1 ? `${g.photos.length} photos` : '1 photo'} <span className="aj-stack-play">▶</span>
                  </span>
                </button>
              </div>
            )}

            {g.voices.map((v) => {
              const side = v.author === 'Anusha' ? 'right' : 'left';
              return (
                <div className={`aj-tl-row ${side}`} key={v.id}>
                  <div className="aj-tl-bubble">
                    <VoiceClip passcode={passcode} v={v} />
                  </div>
                </div>
              );
            })}

            {g.notes.map((en) => {
              const side = en.author === 'Anusha' ? 'right' : 'left';
              return (
                <div className={`aj-tl-row ${side}`} key={en.id}>
                  <div className="aj-tl-bubble">
                    <div className={`aj-entry aj-author-${(en.author || '').toLowerCase()}`}>
                      <div className="aj-entry-top">
                        <span className="aj-entry-author">
                          {en.mood ? `${en.mood} ` : ''}
                          {en.author}
                        </span>
                        <span className="aj-entry-time">{prettyTime(en.createdAt)}</span>
                      </div>
                      <div className="aj-entry-text">{en.text}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {show && (
        <Slideshow
          passcode={passcode}
          photos={show.photos}
          startIndex={show.index}
          autoplay={show.autoplay}
          onClose={closeShow}
        />
      )}
    </div>
  );
}
